import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Paperclip, User, Clock, Play, CheckCircle2, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getTicketAttachmentUrl, useUpdateTicketStatus } from './useTickets';
import { ResolveDialog } from './ResolveDialog';
import { STATUS_BADGE_CLASSES, STATUS_LABELS } from './types';
import type { Ticket } from './types';

const ROLE_LABELS: Record<string, string> = {
  admin_clinance: 'Administrador',
  gerente_operacao: 'Gerente',
  consultor: 'Consultor',
  comercial: 'Comercial',
  cliente: 'Cliente',
};

interface Props {
  ticket: Ticket;
}

export function TicketCard({ ticket }: Props) {
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const updateStatus = useUpdateTicketStatus();

  useEffect(() => {
    let cancelled = false;
    if (ticket.attachment_path && !ticket.attachment_purged) {
      void getTicketAttachmentUrl(ticket).then((url) => {
        if (!cancelled) setAttachmentUrl(url);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [ticket]);

  const isImage = ticket.attachment_type?.startsWith('image/');
  const isVideo = ticket.attachment_type?.startsWith('video/');

  const downloadFilename = (() => {
    if (!ticket.attachment_path) return 'anexo';
    const last = ticket.attachment_path.split('/').pop() ?? 'anexo';
    return last.replace(/^\d+_/, '');
  })();

  const handleDownload = async () => {
    if (!attachmentUrl) return;
    try {
      const res = await fetch(attachmentUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error(`Erro ao baixar anexo: ${e instanceof Error ? e.message : 'desconhecido'}`);
    }
  };

  const handleSetInProgress = () => {
    void updateStatus.mutateAsync({ id: ticket.id, status: 'in_progress' });
  };

  const handleResolve = async (note: string) => {
    await updateStatus.mutateAsync({
      id: ticket.id,
      status: 'resolved',
      ...(note && { note }),
    });
  };

  const handleReopen = () => {
    void updateStatus.mutateAsync({ id: ticket.id, status: 'open' });
  };

  const roleLabel = ticket.reporter_role
    ? (ROLE_LABELS[ticket.reporter_role] ?? ticket.reporter_role)
    : '';

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-foreground">{ticket.title}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <User size={12} />
                {ticket.reporter_name}
                {roleLabel && <span className="text-muted-foreground/70">• {roleLabel}</span>}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock size={12} />
                {format(new Date(ticket.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </span>
            </div>
          </div>
          <Badge
            variant="outline"
            className={`text-xs font-medium ${STATUS_BADGE_CLASSES[ticket.status]}`}
          >
            {STATUS_LABELS[ticket.status]}
          </Badge>
        </div>

        <ExpandableDescription text={ticket.description} />

        {ticket.attachment_path && (
          <div className="rounded-lg border border-border bg-secondary/30 p-2">
            {ticket.attachment_purged ? (
              <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground">
                <Paperclip size={12} />
                Anexo expirado após 30 dias
              </div>
            ) : !attachmentUrl ? (
              <div className="px-2 py-1 text-xs text-muted-foreground">Carregando anexo…</div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
                  <Paperclip size={12} />
                  {isImage ? 'Imagem' : isVideo ? 'Vídeo' : 'Anexo'}
                </span>
                <a
                  href={attachmentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-7 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 text-xs hover:bg-accent hover:text-accent-foreground"
                >
                  <ExternalLink size={12} />
                  Visualizar em pop-up
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownload}
                  className="h-7 text-xs"
                >
                  <Download size={12} className="mr-1.5" />
                  Baixar {downloadFilename}
                </Button>
              </div>
            )}
          </div>
        )}

        {ticket.status === 'resolved' && ticket.resolution_note && (
          <div className="rounded-lg border border-income/30 bg-income/5 p-2 text-xs">
            <span className="font-semibold text-income">Resolução: </span>
            <span className="text-foreground">{ticket.resolution_note}</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {ticket.status === 'open' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSetInProgress}
                disabled={updateStatus.isPending}
              >
                <Play size={14} className="mr-1.5" />
                Iniciar
              </Button>
              <Button
                size="sm"
                variant="income"
                onClick={() => setResolveOpen(true)}
                disabled={updateStatus.isPending}
              >
                <CheckCircle2 size={14} className="mr-1.5" />
                Resolver
              </Button>
            </>
          )}
          {ticket.status === 'in_progress' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={handleReopen}
                disabled={updateStatus.isPending}
              >
                Voltar para Aberto
              </Button>
              <Button
                size="sm"
                variant="income"
                onClick={() => setResolveOpen(true)}
                disabled={updateStatus.isPending}
              >
                <CheckCircle2 size={14} className="mr-1.5" />
                Resolver
              </Button>
            </>
          )}
          {ticket.status === 'resolved' && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleReopen}
              disabled={updateStatus.isPending}
            >
              Reabrir
            </Button>
          )}
        </div>
      </CardContent>

      <ResolveDialog
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        ticketTitle={ticket.title}
        onConfirm={handleResolve}
      />
    </Card>
  );
}

function ExpandableDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <div>
      <p
        ref={ref}
        className={`whitespace-pre-wrap text-sm text-foreground ${expanded ? '' : 'line-clamp-3'}`}
      >
        {text}
      </p>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-primary hover:underline"
        >
          {expanded ? 'Ver menos' : 'Ver mais'}
        </button>
      )}
    </div>
  );
}
